import { type ReactElement, type SetStateAction } from 'react'
import {
  isAccountStatusId,
  isProgramId,
  isRoleId,
  isTeamId,
  isWorkflowActionId,
  isWorkflowStageId,
  type ProgramAutoEnrollmentTrigger,
  type ProgramId,
  type ProgramTrigger,
} from '@terros/common'
import { useSafeCurrentWorkflow } from '@front/model'
import {
  AccountStatusMultiSelect,
  type MultiSelectFilterBehavior,
  RoleMultiSelect,
  TeamMultiSelect,
  WorkflowActionMultiSelect,
  WorkflowStageMultiSelect,
} from '@components'
import { type ProgramTriggerType, toTriggerValues } from './programTriggers'
import { ProgramMultiSelect } from './ProgramMultiSelect'

const BEHAVIOR: MultiSelectFilterBehavior = { emptyBehavior: 'none', allowEmpty: true }

type Props = {
  triggerType: ProgramTriggerType
  triggers: ProgramAutoEnrollmentTrigger[]
  setTriggerValues: (values: ProgramTrigger[]) => void
  programId?: ProgramId
}

export const ProgramTriggerValues = ({
  triggerType,
  triggers,
  setTriggerValues,
  programId,
}: Props): ReactElement | null => {
  const workflowContext = useSafeCurrentWorkflow()

  const applySetState = <T extends ProgramTrigger>(
    input: SetStateAction<T[]>,
    isValue: (value: ProgramTrigger) => value is T
  ): void => {
    const current = toTriggerValues(triggers, isValue)
    setTriggerValues(typeof input === 'function' ? input(current) : input)
  }

  if (triggerType === 'status') {
    return (
      <AccountStatusMultiSelect
        label='Account Stages'
        placeholder='Select account stages'
        statusIds={toTriggerValues(triggers, isAccountStatusId)}
        setStatusIds={setTriggerValues}
      />
    )
  }

  if (triggerType === 'workflowStage') {
    return (
      <WorkflowStageMultiSelect
        hideInitial
        behavior={BEHAVIOR}
        stages={workflowContext?.workflow.stages ?? []}
        placeholder='Select workflow stages'
        inputProps={{ label: 'Workflow Stages' }}
        value={toTriggerValues(triggers, isWorkflowStageId)}
        setValue={(input) => applySetState(input, isWorkflowStageId)}
      />
    )
  }

  if (triggerType === 'workflowAction') {
    return (
      <WorkflowActionMultiSelect
        behavior={BEHAVIOR}
        actions={workflowContext?.workflow.actions ?? []}
        placeholder='Select workflow actions'
        inputProps={{ label: 'Workflow Actions' }}
        value={toTriggerValues(triggers, isWorkflowActionId)}
        setValue={(input) => applySetState(input, isWorkflowActionId)}
      />
    )
  }

  if (triggerType === 'role') {
    return (
      <RoleMultiSelect
        label='Roles'
        placeholder='Select roles'
        value={toTriggerValues(triggers, isRoleId)}
        onChange={setTriggerValues}
      />
    )
  }

  if (triggerType === 'team') {
    return (
      <TeamMultiSelect
        label='Teams'
        placeholder='Select teams'
        value={toTriggerValues(triggers, isTeamId)}
        onChange={setTriggerValues}
      />
    )
  }

  if (triggerType === 'program') {
    return (
      <ProgramMultiSelect
        filterProgramId={programId}
        selectedProgramIds={toTriggerValues(triggers, isProgramId)}
        setProgramIds={setTriggerValues}
      />
    )
  }

  return null
}
